import React, { useState } from 'react';
import PropTypes from 'prop-types';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import PortalDropdown, { DropdownMenuItem } from 'views_shared/app/javascript/components/PortalDropdown';
import ConfirmationModal from 'views_shared/app/javascript/components/ConfirmationModal';

function ConversationDropdown({
  conversation,
  onActionComplete,
  small = false,
  onAddContact,
  onToggleSidebar,
  sidebarExpanded = false,
  activeMailbox = null,
}) {
  const [showReportModal, setShowReportModal] = useState(false);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // The Focused/Everything mailbox split only exists for media users; hide the
  // "Move to/Remove from Focused" action for promoters.
  const isMedia = globalThis.Qwoted.CURRENT_PROFILE_TYPE === 'type_media';

  const handleToggleReadStatus = async () => {
    setIsProcessing(true);
    const isRead = Boolean(conversation.receipt_read_at);
    const apiCall = isRead ? qapi.conversations.markUnreadById : qapi.conversations.markReadById;
    const label = isRead ? 'unread' : 'read';
    const newReadAt = isRead ? null : new Date().toISOString();

    try {
      const result = await apiCall(conversation.conversation_id);

      if (result.error) {
        throw new Error(result.error.statusText || `Failed to mark as ${label}`);
      }

      globalThis.Qwoted.showToast({
        toastTitle: `Marked as ${label}`,
        toastBody: `Conversation marked as ${label}`,
        variant: 'success',
      });

      onActionComplete({
        patches: [{ id: conversation.conversation_id, fields: { receipt_read_at: newReadAt } }],
      });
    } catch (error) {
      qlog.error(`Failed to mark as ${label}:`, error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: `Failed to mark conversation as ${label}. Please try again.`,
        variant: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // PostgREST exposes muted_at; mat-backed payloads may use receipt_muted_at only.
  const isMuted = Boolean(conversation.muted_at ?? conversation.receipt_muted_at);
  const isFlagged = Boolean(conversation.flagged_at ?? conversation.receipt_flagged_at);

  const handleMute = async () => {
    setIsProcessing(true);

    try {
      const apiCall = isMuted ? qapi.conversations.unmuteById : qapi.conversations.muteById;
      const result = await apiCall(conversation.conversation_id);

      if (result.error) {
        throw new Error(result.error.statusText || 'Failed to update mute status');
      }

      const action = isMuted ? 'Unmuted' : 'Muted';
      globalThis.Qwoted.showToast({
        toastTitle: action,
        toastBody: `Conversation ${action.toLowerCase()} successfully`,
        variant: 'success',
      });

      const newMutedAt = isMuted ? null : new Date().toISOString();
      onActionComplete({
        patches: [{ id: conversation.conversation_id, fields: { receipt_muted_at: newMutedAt } }],
      });
    } catch (error) {
      qlog.error('Failed to toggle mute:', error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to update mute status. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReport = async () => {
    setIsProcessing(true);

    try {
      const result = await qapi.conversations.flagById(conversation.conversation_id);

      if (result.error) {
        throw new Error(result.error.statusText || 'Failed to report conversation');
      }

      setShowReportModal(false);
      globalThis.Qwoted.showToast({
        toastTitle: 'Reported',
        toastBody: 'Conversation reported for review',
        variant: 'success',
      });

      onActionComplete({
        patches: [{ id: conversation.conversation_id, fields: { receipt_flagged_at: new Date().toISOString() } }],
      });
    } catch (error) {
      qlog.error('Failed to report conversation:', error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to report conversation. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTrash = async () => {
    setIsProcessing(true);

    try {
      const result = await qapi.conversations.archiveById(conversation.conversation_id);

      if (result.error) {
        throw new Error(result.error.statusText || 'Failed to move conversation to trash');
      }

      setShowTrashModal(false);
      globalThis.Qwoted.showToast({
        toastTitle: 'Moved to Trash',
        toastBody: 'Conversation moved to trash',
        variant: 'success',
      });

      onActionComplete({ evictedIds: [conversation.conversation_id] });
    } catch (error) {
      qlog.error('Failed to move conversation to trash:', error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to move conversation to trash. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMoveMailbox = async () => {
    setIsProcessing(true);

    const currentMailbox = conversation.conversation_mailbox || 'focused';
    const targetMailbox = currentMailbox === 'focused' ? 'other' : 'focused';

    try {
      const result = await qapi.conversationsMailbox.updateMailbox(conversation.conversation_id, targetMailbox);

      if (result.error) {
        throw new Error(result.error.statusText || 'Failed to move conversation');
      }

      const targetLabel = targetMailbox === 'focused' ? 'Focused' : 'Everything';
      globalThis.Qwoted.showToast({
        toastTitle: 'Moved',
        toastBody: `Conversation moved to ${targetLabel}`,
        variant: 'success',
      });

      // Filter-aware patchConversations evicts when the new mailbox doesn't match the
      // active filter (Focused → other) and keeps it otherwise (Everything tab).
      onActionComplete({
        patches: [{ id: conversation.conversation_id, fields: { conversation_mailbox: targetMailbox } }],
      });
    } catch (error) {
      qlog.error('Failed to move conversation:', error);

      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to move conversation. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Determine icons and labels based on conversation state
  const muteIcon = isMuted ? 'fa-regular fa-volume' : 'fa-regular fa-volume-slash';
  const muteLabel = isMuted ? 'Unmute Conversation' : 'Mute Conversation';

  // Mobile-only sidebar toggle label/icon
  const sidebarToggleIcon = 'fa-regular fa-sidebar-flip';
  const sidebarToggleLabel = sidebarExpanded ? 'Hide Profile' : 'Show Profile';

  const moveIcon = conversation.conversation_mailbox === 'focused'
      ? 'fa-regular fa-arrow-turn-right'
      : 'fa-regular fa-arrow-turn-left';
  const moveLabel = conversation.conversation_mailbox === 'focused' ? 'Remove from Focused' : 'Move to Focused';
  // On the Everything tab, hide "Remove from Focused" — items there have no visual cue indicating they're in Focused, so the option is confusing. "Move to Focused" stays visible for items currently in Other.
  const hideMoveOnEverything = activeMailbox === 'everything' && conversation.conversation_mailbox === 'focused';

  const flagIcon = 'fa-regular fa-flag';
  const flagLabel = isFlagged ? 'Reported' : 'Report Conversation';

  return (
    <>
      <PortalDropdown
        disabled={isProcessing}
        toggleClassName="btn btn-secondary btn-icon rounded-2 text-neutral-80 fs-5 border-0 shadow-none"
        toggleStyle={small ? { width: 28, height: 28, '--bs-btn-bg': 'transparent' } : undefined}
      >
        {/* Mobile-only: actions promoted from the header toolbar below lg. */}
        {onAddContact && (
          <DropdownMenuItem
            className="d-md-none"
            icon="fa-regular fa-user-plus"
            label="Add to Conversation"
            onClick={onAddContact}
            disabled={isProcessing}
          />
        )}

        {onToggleSidebar && (
          <DropdownMenuItem
            className="d-md-none"
            icon={sidebarToggleIcon}
            label={sidebarToggleLabel}
            onClick={onToggleSidebar}
            disabled={isProcessing}
          />
        )}

        <DropdownMenuItem
          icon={conversation.receipt_read_at ? 'fa-regular fa-envelope' : 'fa-regular fa-envelope-open'}
          label={conversation.receipt_read_at ? 'Mark as Unread' : 'Mark as Read'}
          onClick={handleToggleReadStatus}
          disabled={isProcessing}
        />

        <DropdownMenuItem icon={muteIcon} label={muteLabel} onClick={handleMute} disabled={isProcessing} />

        {isMedia && !hideMoveOnEverything && (
          <DropdownMenuItem icon={moveIcon} label={moveLabel} onClick={handleMoveMailbox} disabled={isProcessing} />
        )}

        <DropdownMenuItem
          icon={flagIcon}
          label={flagLabel}
          onClick={() => setShowReportModal(true)}
          disabled={isProcessing || isFlagged}
        />

        <DropdownMenuItem
          icon="fa-regular fa-trash"
          label="Delete Conversation"
          onClick={() => setShowTrashModal(true)}
          disabled={isProcessing}
        />
      </PortalDropdown>

      <ConfirmationModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        onConfirm={handleReport}
        title="Report Conversation"
        message="Are you sure you want to report this conversation as inappropriate?"
        detail="Our team will review this conversation and take appropriate action if needed."
        confirmLabel="Report"
        confirmingLabel="Reporting..."
        isProcessing={isProcessing}
      />

      <ConfirmationModal
        show={showTrashModal}
        onHide={() => setShowTrashModal(false)}
        onConfirm={handleTrash}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation?"
        detail="This conversation will be moved to your Trash. Other participants will still see it. You can restore it within 30 days before it’s permanently deleted."
        confirmLabel="Yes, Delete"
        confirmingLabel="Deleting..."
        variant="danger"
        isProcessing={isProcessing}
      />
    </>
  );
}

ConversationDropdown.propTypes = {
  conversation: PropTypes.shape({
    conversation_id: PropTypes.string.isRequired,
    conversation_mailbox: PropTypes.string,
    receipt_read_at: PropTypes.string,
    muted_at: PropTypes.string,
    receipt_muted_at: PropTypes.string,
    flagged_at: PropTypes.string,
    receipt_flagged_at: PropTypes.string,
  }).isRequired,
  onActionComplete: PropTypes.func.isRequired,
  small: PropTypes.bool,
  onAddContact: PropTypes.func,
  onToggleSidebar: PropTypes.func,
  sidebarExpanded: PropTypes.bool,
  activeMailbox: PropTypes.string,
};

export default ConversationDropdown;
